import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  ClipboardList,
  Handshake,
  FileText,
  Receipt,
  PoundSterling,
  CalendarClock,
  ListChecks,
  HeartHandshake,
  MessageSquare,
  ChevronRight,
  ArrowDown,
  AlertTriangle,
  CheckCircle2,
  BookOpen,
  ShieldCheck,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import {
  WorkspaceShell,
  WorkspaceSupportBlock,
} from "../../components/workspace/WorkspaceShell";
import { DriveLink } from "../../components/workspace/HubText";
import { NATIVE_FORM_IDS, nativeFormPath } from "../../data/nativeFormTemplateIds";

/* ────────────────────────────────────────────────────────────────────────
   Family Circle Hub — /workspace/family-circle-hub
   Paid step after the Family Starter Hub. Uses the same workspace primitives
   as the other three hubs. Purple accent matches the family side.
   ──────────────────────────────────────────────────────────────────────── */

// ── Reusable bits (kept inline to match the family-hub pattern) ────────────
const Para = ({ children }) => (
  <p className="text-ink-muted leading-relaxed mt-4 first:mt-0">{children}</p>
);
const H4 = ({ children }) => (
  <h4 className="font-display text-base font-semibold text-ink mt-5">{children}</h4>
);
const Bullets = ({ items }) => (
  <ul className="mt-3 space-y-2">
    {items.map((it) => (
      <li key={it} className="flex gap-3 text-ink-muted leading-relaxed">
        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-purple" strokeWidth={1.75} aria-hidden="true" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);
const scrollToId = (id) => (e) => {
  if (e) e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// ── Start Here — 3 first-step cards (anchors below) ────────────────────────
const START_HERE = [
  {
    icon: ClipboardList,
    title: "Post your care needs",
    body: "Share clear care needs with JCC and let interested Pro carers respond directly.",
    cta: "Post a care need",
    target: "care-needs",
  },
  {
    icon: FileText,
    title: "Set up clear agreements",
    body: "Use the five templates below in the recommended order — clarity from day one.",
    cta: "View templates",
    target: "agreements",
  },
  {
    icon: Handshake,
    title: "Enter your Care Partnership Room",
    body: "Once you and your carer have chosen to work together, move into your private shared space.",
    cta: "Open partnership room",
    target: "partnership-room",
  },
];

// ── Templates / agreements — recommended order. Titles/summaries/body
// preserved exactly; only the destination (href) changed from Drive to
// the native JCC form. ────────────────────────────────────────────────
const TEMPLATES = [
  {
    id: "care-booking",
    icon: FileText,
    title: "Care Booking Agreement",
    summary: "Sets out the core terms of the placement before care begins.",
    href: nativeFormPath(NATIVE_FORM_IDS.CARE_BOOKING_AGREEMENT),
    body: (
      <>
        <Para><strong>What this document is for.</strong> The Care Booking Agreement sets out the core terms of a care arrangement before care begins. It confirms start date, duration, agreed rate and payment schedule, scope of care, notice terms and general working expectations. This document forms the foundation of a clear, professional working relationship.</Para>
        <H4>Why it matters</H4>
        <Para>Many care disagreements arise not from bad intentions, but from assumptions. When care terms are discussed verbally only, misunderstandings can occur around hours and availability, overnight expectations, payment dates and what is included within the agreed role. Putting these terms in writing protects both family and carer and creates clarity from day one.</Para>
        <H4>When to use it</H4>
        <Bullets items={[
          "At the start of every new placement",
          "Before deposits are paid",
          "Before travel arrangements are made",
          "Whenever a new booking period is agreed",
        ]} />
        <Para>Both parties should keep signed copies.</Para>
        <div className="mt-5 rounded-2xl bg-purple/5 border border-purple/20 p-4 text-sm text-ink-muted leading-relaxed">
          <strong className="text-ink">Professional standard.</strong> Care should never begin on the basis of informal verbal agreement alone. Clear written terms protect everyone involved.
        </div>
      </>
    ),
  },
  {
    id: "responsible-payer",
    icon: ShieldCheck,
    title: "Responsible Payer Confirmation Form",
    summary: "Confirms in writing who is legally and financially responsible for paying.",
    href: nativeFormPath(NATIVE_FORM_IDS.RESPONSIBLE_PAYER_CONFIRMATION),
    body: (
      <>
        <Para><strong>What this document is for.</strong> Clearly identifies who is legally and financially responsible for payment of the agreed care fees. In many care arrangements, the person receiving care is not the person managing or paying for it. This form removes ambiguity by confirming, in writing, who accepts responsibility for payment.</Para>
        <H4>Why it matters</H4>
        <Para>Most payment disputes in independent care do not arise from bad intentions — they arise from assumptions. Common situations include a family member verbally agreeing to “sort the payments”, shared responsibility between siblings, Direct Payments being delayed, Power of Attorney holders acting informally, or confusion about who is actually accountable. Without written confirmation, carers may complete work without clear financial protection.</Para>
        <H4>When to use it</H4>
        <Bullets items={[
          "At the start of every new placement",
          "Alongside the Care Booking Agreement",
          "Before deposits or travel arrangements are finalised",
          "Whenever the responsible payer changes",
        ]} />
        <Para>The signed form should be retained by both parties.</Para>
      </>
    ),
  },
  {
    id: "deposit-payment",
    icon: PoundSterling,
    title: "Deposit & Payment Agreement",
    summary: "Confirms the agreed rate, payment frequency, deposit and late-payment expectations.",
    href: nativeFormPath(NATIVE_FORM_IDS.DEPOSIT_PAYMENT_AGREEMENT),
    body: (
      <>
        <Para><strong>What this document is for.</strong> Confirms the agreed care rate, payment frequency, deposit arrangements (if applicable) and expectations around late payment. It ensures financial terms are clearly agreed before care begins. Works alongside the Care Booking Agreement and Responsible Payer Confirmation Form to create complete financial clarity.</Para>
        <H4>Why it matters</H4>
        <Para>Money misunderstandings are one of the most common causes of tension in independent care arrangements. Without clear written agreement, confusion can arise around the agreed daily or hourly rate, when payments are due, whether a deposit is required, and what happens if care is cancelled or delayed. Clarity at the beginning prevents awkward conversations later.</Para>
        <H4>When to use it</H4>
        <Bullets items={[
          "At the start of every new placement",
          "Before care begins",
          "Before travel arrangements are made",
          "Whenever rates or payment frequency change",
        ]} />
      </>
    ),
  },
  {
    id: "schedule-expectations",
    icon: ListChecks,
    title: "Care Schedule & Expectations Template",
    summary: "Records the agreed daily structure, duties and boundaries.",
    href: nativeFormPath(NATIVE_FORM_IDS.CARE_SCHEDULE_EXPECTATIONS),
    body: (
      <>
        <Para><strong>What this document is for.</strong> Records the agreed daily structure, duties and boundaries of a care arrangement. Provides a clear outline of what is included within the agreed role and helps both family and carer understand how care will be delivered in practice.</Para>
        <H4>Why it matters</H4>
        <Para>Many care arrangements begin positively but become strained when expectations are unclear. Common areas of misunderstanding include daily start and finish times, break periods, overnight availability, household duties, level of personal care required and additional responsibilities not originally discussed. Without written clarity, responsibilities can gradually expand without formal agreement.</Para>
        <H4>When to use it</H4>
        <Bullets items={[
          "At the start of every new placement",
          "When agreeing live-in arrangements",
          "When care hours change",
          "When responsibilities increase or reduce",
        ]} />
        <Para>Review alongside the Care Booking Agreement and update using the Change of Care Terms Form where necessary.</Para>
      </>
    ),
  },
  {
    id: "change-of-care",
    icon: CalendarClock,
    title: "Change of Care Terms Form",
    summary: "Used if care needs or terms evolve during a placement.",
    href: nativeFormPath(NATIVE_FORM_IDS.CHANGE_OF_CARE_TERMS),
    body: (
      <>
        <Para>Care needs rarely stay exactly the same. A loved one may become less mobile, require additional support, experience changes in cognition, develop new continence needs, or simply need more care than was originally agreed. When these changes happen, it&apos;s important that everyone involved understands what has changed and how it may affect the arrangement.</Para>
        <Para>The Change in Care Notification Form provides a simple way for families and carers to record significant changes in care needs, ensuring that expectations, responsibilities and support requirements remain clear and up to date.</Para>
        <H4>Using this form helps</H4>
        <Bullets items={[
          "Maintain open and honest communication between families and carers",
          "Identify when care arrangements may need reviewing",
          "Ensure care remains safe, appropriate and sustainable",
          "Create a clear record of changes for future reference",
          "Prevent misunderstandings about duties, hours, fees or support needs",
        ]} />
        <Para>Where a significant change is identified, families and carers should discuss whether any adjustments to care arrangements, working patterns, support requirements or fees may be needed.</Para>
      </>
    ),
  },
];

export default function FamilyCircleHub() {
  const [openTemplate, setOpenTemplate] = useState("");
  const openTpl = (id) => {
    setOpenTemplate((prev) => (prev === id ? "" : id));
    setTimeout(() => {
      const el = document.getElementById(`tpl-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <WorkspaceShell
      testId="hub-family-circle"
      eyebrow="Family Care Office Hub"
      memberBadge="Family Care Office member"
      accent="purple"
      title="Welcome to Family Care Office"
      intro="Your paid family workspace — practical tools and guidance to help you arrange and manage independent care clearly, ethically and confidently. You remain in control. These tools are here to support you, not rush you."
    >
      {/* ── JCC Forms Library quick-access ───────────────────────────────── */}
      <Link
        to="/forms"
        data-testid="family-circle-forms-library-banner"
        className="group flex items-center justify-between gap-4 rounded-2xl border border-purple/20 bg-purple/5 p-5 hover:border-purple transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple/10 text-purple flex items-center justify-center shrink-0">
            <FileText className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div>
            <h3 className="font-display font-semibold">JCC Forms Library</h3>
            <p className="text-sm text-ink-muted">Complete, save and print your interactive JCC forms.</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-purple shrink-0 group-hover:translate-x-0.5 transition-transform" />
      </Link>

      {/* ── Reassurance panel ────────────────────────────────────────────── */}
      <section
        data-testid="family-circle-reassurance"
        className="rounded-3xl border border-purple/15 bg-purple/5 p-6 md:p-8 -mt-2"
      >
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          <div>
            <div className="overline text-purple mb-2">Your membership level</div>
            <h3 className="font-display text-lg font-semibold">Current plan: Family Care Office — £19/month</h3>
            <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">
              Free Family resources continue to sit alongside these paid tools — learning and doing can happen side by side.
            </p>
          </div>
          <div>
            <div className="overline text-purple mb-2">What this hub is</div>
            <h3 className="font-display text-lg font-semibold">Structure, clarity and peace of mind</h3>
            <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">
              Not an agency service. JCC doesn&apos;t place carers, take control of decisions or lock you into contracts.
            </p>
          </div>
          <div>
            <div className="overline text-purple mb-2">As your situation changes</div>
            <h3 className="font-display text-lg font-semibold">Use it at your own pace</h3>
            <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">
              Whether you&apos;re planning ahead, arranging care now or adjusting an existing arrangement, return whenever you need guidance or reassurance.
            </p>
          </div>
        </div>
      </section>

      {/* ── Start Here ──────────────────────────────────────────────────── */}
      <section id="start-here" data-testid="family-circle-start-here" className="scroll-mt-24">
        <div className="max-w-2xl mb-5">
          <div className="overline text-purple mb-2">Start here</div>
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            A clear, calm order to follow
          </h2>
          <p className="mt-1.5 text-ink-muted leading-relaxed">
            Three first steps — take them in your own time. Each card jumps to the right place on this page.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {START_HERE.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.target}
                href={`#${c.target}`}
                onClick={scrollToId(c.target)}
                data-testid={`start-here-${c.target}`}
                className="block card-base p-6 flex flex-col group focus:outline-none focus:ring-2 focus:ring-purple/40 focus:ring-offset-2"
              >
                <div className="w-11 h-11 rounded-xl bg-purple/10 text-purple flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display text-lg font-semibold leading-snug">{c.title}</h3>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">{c.body}</p>
                <span className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-sm font-semibold text-purple">
                  {c.cta}
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                </span>
              </a>
            );
          })}
        </div>
      </section>

      {/* ── Post a care need (JCC-native posting form) ──────────────────── */}
      <section id="care-needs" data-testid="family-circle-care-needs" className="scroll-mt-24">
        <div className="rounded-3xl border border-purple/30 bg-gradient-to-br from-purple/5 via-white to-purple/5 p-7 md:p-9">
          <h2 className="font-display text-xl md:text-2xl font-semibold">Post your care needs</h2>
          <p className="mt-2 text-sm text-ink-muted leading-relaxed max-w-2xl">
            Prefer to post your care need directly and manage it yourself? Save a draft
            privately, send it to JCC for review, and manage your post — pause, mark
            matched, or relist — from one place. JCC checks each post before sharing
            it with any carer.
          </p>
          <a href="/family/care-needs"
             data-testid="family-circle-care-needs-cta"
             className="mt-4 inline-flex items-center gap-2 rounded-xl bg-purple px-4 py-2 text-sm font-semibold text-white hover:bg-purple/90">
            Post a care need
          </a>
        </div>
      </section>

      {/* ── Browse Carer Profiles ─────────────────────────────────────── */}
      <section data-testid="family-circle-carer-profiles" className="scroll-mt-24">
        <div className="rounded-3xl border border-slate-200 bg-white p-7 md:p-9">
          <h2 className="font-display text-xl md:text-2xl font-semibold">Browse Carer Profiles</h2>
          <p className="mt-2 text-sm text-ink-muted leading-relaxed max-w-2xl">
            Look through approved Pro Carer Profiles from independent carers on Just Care Community. No contact
            details are shown here — post a care need above to hear from interested carers directly.
          </p>
          <a href="/family/carer-profiles"
             data-testid="family-circle-carer-profiles-cta"
             className="mt-4 inline-flex items-center gap-2 rounded-xl border-2 border-purple/30 text-purple px-4 py-2 text-sm font-semibold hover:bg-purple/5">
            Browse Carer Profiles
          </a>
        </div>
      </section>

      {/* ── Setting up a safe care arrangement (agreements) ─────────────── */}
      <section id="agreements" data-testid="family-circle-agreements" className="scroll-mt-24">
        <div className="max-w-2xl mb-5">
          <div className="overline text-purple mb-2">Setting up a safe care arrangement</div>
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            Family agreement templates — used in the recommended order
          </h2>
          <p className="mt-1.5 text-ink-muted leading-relaxed">
            Pro carers have their own professional contracts. These family templates are designed to work alongside them — to build a shared agreement, align documents before care begins, and keep signed copies for both parties. Not every situation needs every document, but clarity at the start prevents most misunderstandings.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-7">
          {TEMPLATES.map((t, i) => {
            const Icon = t.icon;
            const isOpen = openTemplate === t.id;
            return (
              <button
                type="button"
                key={t.id}
                onClick={() => openTpl(t.id)}
                aria-expanded={isOpen}
                aria-controls={`tpl-${t.id}`}
                data-testid={`tpl-card-${t.id}`}
                className={`text-left card-base p-6 flex flex-col group transition-shadow focus:outline-none focus:ring-2 focus:ring-purple/40 focus:ring-offset-2 hover:shadow-md ${
                  isOpen ? "ring-2 ring-purple/40" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-purple/10 text-purple flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <span className="pill bg-purple/10 text-purple">Step {i + 1}</span>
                </div>
                <h3 className="font-display text-lg font-semibold leading-snug">{t.title}</h3>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">{t.summary}</p>
                <span className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-sm font-semibold text-purple">
                  {isOpen ? "Reading guide ↓" : "Open guide"}
                  <ChevronRight
                    className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : "group-hover:translate-x-0.5"}`}
                    strokeWidth={2}
                  />
                </span>
              </button>
            );
          })}
        </div>

        <Accordion
          type="single"
          collapsible
          value={openTemplate}
          onValueChange={(v) => setOpenTemplate(v)}
          className="space-y-3"
          data-testid="family-circle-templates-accordion"
        >
          {TEMPLATES.map((t) => {
            const Icon = t.icon;
            return (
              <AccordionItem
                key={t.id}
                value={t.id}
                id={`tpl-${t.id}`}
                className="rounded-2xl border border-slate-200 bg-white px-5 md:px-7 scroll-mt-24"
              >
                <AccordionTrigger className="py-5 hover:no-underline">
                  <span className="flex items-center gap-3 text-left">
                    <Icon className="w-5 h-5 text-purple shrink-0" strokeWidth={1.75} />
                    <span className="font-display text-lg font-semibold">{t.title}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-7 pt-1 text-base leading-relaxed">
                  {t.body}
                  <div className="mt-6">
                    <DriveLink href={t.href} label="Download PDF" variant="download" testId={`tpl-download-${t.id}`} />
                  </div>
                  <p className="mt-3 text-xs text-ink-soft leading-relaxed">
                    Provided as structured guidance to support independent care arrangements. Not a substitute for independent legal advice.
                  </p>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </section>

      {/* ── Your Care Partnership Room ──────────────────────────────────── */}
      <section id="partnership-room" data-testid="family-circle-partnership-room" className="scroll-mt-24">
        <div className="relative overflow-hidden rounded-3xl border border-purple/20 bg-gradient-to-br from-purple/5 via-white to-purple/5 p-7 md:p-10">
          <div className="absolute left-0 top-0 h-full w-1.5 bg-purple" aria-hidden="true" />
          <div className="overline text-purple mb-3">Your Care Partnership Room</div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight max-w-2xl">
            A private shared space — for when you and your carer have chosen to work together
          </h2>
          <p className="mt-3 text-ink-muted leading-relaxed max-w-3xl">
            Once you and a carer have agreed to work together, your Care Partnership Room becomes the private workspace for the practical side of the arrangement — agreed documents, important information, records and ongoing communication. JCC does not choose, employ, manage or supervise the carer; the room simply gives the two of you a calm, organised place to keep things clear.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href="/workspace/family-circle/placements"
              data-testid="enter-partnership-room"
              className="btn-purple"
            >
              Enter Your Care Partnership Room
              <ChevronRight className="w-4 h-4" strokeWidth={2} />
            </a>
            <span className="text-xs text-ink-soft">Opens your Care Placements dashboard (sign-in required).</span>
          </div>
          <div className="mt-6 pt-6 border-t border-purple/10 flex items-start gap-3" data-testid="family-circle-arrangement-record-note">
            <Sparkles className="w-5 h-5 text-purple shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden="true" />
            <p className="text-sm text-ink-muted leading-relaxed max-w-2xl">
              <strong className="text-ink">Decided to go ahead with a carer?</strong> Once your Care Partnership
              Room is activated, you'll see a prompt inside it to set up your <strong className="text-ink">Care
              Arrangement</strong> — a clear record of exactly what you've agreed, including rate, hours and
              responsibilities.
            </p>
          </div>
        </div>
      </section>

      {/* ── Useful guides — small Free-Family reminder + revisit basics ─── */}
      <section id="guides" data-testid="family-circle-guides" className="scroll-mt-24">
        <div className="max-w-2xl mb-5">
          <div className="overline text-purple mb-2">Useful guides &amp; reading</div>
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            Need to revisit the basics?
          </h2>
          <p className="mt-1.5 text-ink-muted leading-relaxed">
            Your Free Family resources continue to be available alongside these paid tools.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <a
            href="/workspace/family-starter-hub"
            data-testid="link-family-starter"
            className="block card-base p-6 group focus:outline-none focus:ring-2 focus:ring-purple/40 focus:ring-offset-2"
          >
            <div className="w-11 h-11 rounded-xl bg-purple/10 text-purple flex items-center justify-center mb-3">
              <BookOpen className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <h3 className="font-display text-lg font-semibold">Family Starter Hub</h3>
            <p className="mt-2 text-sm text-ink-muted leading-relaxed">
              The free starting place — understanding independent care, finding a carer, preparing for care.
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-purple">
              Open hub
              <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
            </span>
          </a>
          <a
            href="/care-discussion-room"
            data-testid="link-care-discussion"
            className="block card-base p-6 group focus:outline-none focus:ring-2 focus:ring-purple/40 focus:ring-offset-2"
          >
            <div className="w-11 h-11 rounded-xl bg-purple/10 text-purple flex items-center justify-center mb-3">
              <MessageSquare className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <h3 className="font-display text-lg font-semibold">Care Discussion Room</h3>
            <p className="mt-2 text-sm text-ink-muted leading-relaxed">
              Before you commit to a long-term arrangement, use a time-limited private room to talk things through clearly with a potential carer.
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-purple">
              Read more
              <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
            </span>
          </a>
        </div>
      </section>

      {/* ── Support / next steps (shared component, matches other hubs) ── */}
      <WorkspaceSupportBlock />

      {/* ── Footer note ─────────────────────────────────────────────────── */}
      <section data-testid="family-circle-disclaimer" className="rounded-3xl border border-slate-200 bg-[#f7f7f3] p-6 md:p-8">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden="true" />
          <p className="text-xs md:text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">Just Care Community is not a care agency,</strong> does not employ or manage carers, and does not provide regulated care services. Templates are provided as structured guidance and do not constitute legal advice. All care arrangements are agreed directly between families and carers.
          </p>
        </div>
      </section>
    </WorkspaceShell>
  );
}
